import React from "react";
import { Control, Controller, UseFormWatch } from "react-hook-form";
import ControllerInput from "./ControllerInput";
import TimeWindowController from "./TimeWindowController";
import {
  Shipment,
  ShipmentStopType,
} from "@api/graphql/generated/generated-types";
import { DatePicker } from "@src/common/components";
import StopDateSelector from "@src/orders/create/components/CreateStopsForm/components/StopDateSelector";
import { findIfStopHasFutureDateDelivery } from "@src/common/lib/shipmentStateFinders";

interface StandardTimeRowProps {
  stopIndex: number;
  stopPathName: string;
  stopType: ShipmentStopType;
  isRecurring: boolean;
  selectedDuration: string | undefined;
  onStopDateChange: (() => void) | undefined;
  selectedShipment: Shipment | undefined;
  control: Control;
  watch: UseFormWatch<any>;
  getValues: (name: string) => any;
  formatTime: (time: number) => string | undefined;
  watchOpenTime: any;
  watchCloseTime: any;
  isDeliverByRequired?: boolean;
  isSuiteRequired?: boolean;
  errors?: any;
  clearErrors?: (name: string) => void;
}

function StandardTimeRow({
  stopIndex,
  stopPathName,
  stopType,
  isRecurring,
  selectedDuration,
  onStopDateChange,
  selectedShipment,
  control,
  watch,
  getValues,
  formatTime,
  watchOpenTime,
  watchCloseTime,
  isDeliverByRequired = false,
  isSuiteRequired = false,
  errors,
  clearErrors,
}: StandardTimeRowProps) {
  const hasFutureDateDelivery =
    selectedShipment &&
    findIfStopHasFutureDateDelivery(
      getValues(`${stopPathName}[${stopIndex}]`),
      selectedShipment
    );

  const shouldShowDeliverByDate =
    (stopType === ShipmentStopType.DropOff ||
      stopType === ShipmentStopType.Return) &&
    (hasFutureDateDelivery || selectedDuration === "hasStopDates");

  return (
    <>
      {/* Suite and Route Date row */}
      <div className="col-span-1 flex">
        <ControllerInput
          name={`${stopPathName}[${stopIndex}].suite`}
          label={"Suite"}
          className={"flex-1"}
          required={isSuiteRequired}
        />

        {selectedDuration === "hasStopDates" && (
          <div className={"flex-1 ml-2"}>
            <StopDateSelector
              name={`${stopPathName}[${stopIndex}].stopDate`}
              stopType={stopType}
              control={control}
              selectedDuration={selectedDuration}
              onStopDateChange={onStopDateChange}
              index={stopIndex}
            />
          </div>
        )}
      </div>

      {/* Time window row */}
      <div
        className="col-span-1 flex"
        key={watch(`${stopPathName}[${stopIndex}].timeWindow`)}
      >
        {stopType === ShipmentStopType.PickUp && (
          <TimeWindowController
            stopIndex={stopIndex}
            label="Ready"
            name={`${stopPathName}[${stopIndex}].timeWindow.${
              isRecurring ? "openSecondsSinceMidnightUtc" : "open"
            }`}
            control={control}
            formatTime={formatTime}
            watchValue={watchOpenTime}
            className="flex-1"
          />
        )}
        {stopType === ShipmentStopType.DropOff && (
          <TimeWindowController
            stopIndex={stopIndex}
            label="Deliver By"
            name={`${stopPathName}[${stopIndex}].timeWindow.${
              isRecurring ? "closeSecondsSinceMidnightUtc" : "close"
            }`}
            control={control}
            formatTime={formatTime}
            watchValue={watchCloseTime}
            className={"flex-1"}
            required={isDeliverByRequired}
            requiredMessage="Deliver By Time is required"
            errors={errors}
            clearErrors={clearErrors}
          />
        )}
        {shouldShowDeliverByDate && (
          <div className="flex-1 ml-2 mt-4">
            <Controller
              control={control}
              name={`${stopPathName}[${stopIndex}].deliverByDate`}
              render={({ field }) => (
                <DatePicker
                  size="mini"
                  value={watch(`${stopPathName}[${stopIndex}].deliverByDate`)}
                  formatString={"MMM do, yyyy"}
                  onChange={({ date }) => field.onChange(date)}
                  autoFocusCalendar={false}
                />
              )}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default StandardTimeRow;
